import { NgModule } from '@angular/core';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { split, ApolloClientOptions, InMemoryCache, ApolloLink, fromPromise } from '@apollo/client/core';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { HttpErrorResponse } from '@angular/common/http';
import { timer } from 'rxjs';
import _ from 'underscore';
import { CookieService } from 'ng2-cookies';

import { AuthService, AlertService, ProblemsInterceptorService } from './shared/services';
import { environment } from 'src/environments/environment';
import { AuthCookieService } from './shared/services/cookies.service';
import { SeverityError } from './shared/enums/severity-error';

const path = `${environment.apiURL}/graphql`;
const uri = `https://${path}`;

@NgModule({
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory(httpLink: HttpLink, authService: AuthService, alertService: AlertService, cookieService: AuthCookieService, 
         problemsInterceptorService: ProblemsInterceptorService): ApolloClientOptions<any> {
        const basic = setContext((operation, context) => ({
          headers: {
            Authorization: `Bearer `,
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Credentials': 'true',
            'Access-Control-Allow-Headers': 'Origin, X-Requested-With, X-forwarded-for, Content-Type, Accept, Authorization',
            'Access-Control-Allow-Methods': 'GET,POST,PUT,PATCH,DELETE',
            'Content-Type': 'application/json;charset=utf-8',
            responseType: 'blob' as 'json',
          }
        }));
        const auth = setContext(async () => {
          // Renew the Firebase ID token at the moment of use (cached when still valid, refreshed
          // via the long-lived refresh token when near expiry). This guarantees the request never
          // goes out with an expired token, even after the tab was hidden or the device slept.
          const token = await authService.getValidToken();
          if (!token) {
            return {};
          }
          return {
            headers: {
              Authorization: `Bearer ${token}`,
              'ngsw-bypass': 'true',
            }
          };
        });
        // Create a WebSocket link:
        const ws = new WebSocketLink({
          uri: `wss://${path}`,
          options: {
            timeout: 100000,
            reconnect: true,
            lazy: true,
            connectionParams: async () => {
              // Read a fresh token per (re)connection instead of one captured at module load.
              const token = await authService.getValidToken();
              return {
                token: `Bearer ${token ?? ''}`,
                'ngsw-bypass': 'true',
              };
            },
          },
        });
        const isAuthExpired = (message?: string): boolean => {
          const s = (message || '').toLowerCase();
          return (
            s.includes('authentication is required') ||
            s.includes('jwt expired') ||
            s.includes('token expired') ||
            s.includes('context creation failed')
          );
        };
        const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
          // The AVD-only feed (/avd: readAvdResources WITHOUT an organization) drives its own
          // expired-logon UX (redirect to /login/avd), so it is left untouched here.
          const isAvdOnlyFeed =
            operation?.operationName === 'readAvdResources' && !operation?.variables?.organization;
          const netErrorMsgs: string[] =
            networkError instanceof HttpErrorResponse && networkError.error?.errors
              ? _.pluck(networkError.error.errors, 'message')
              : [];
          const hasAuthError =
            (graphQLErrors?.some((e) => isAuthExpired(e.message)) ?? false) ||
            (networkError instanceof HttpErrorResponse &&
              (networkError.status === 401 || netErrorMsgs.some(isAuthExpired)));

          // Expired Firebase ID token: renew once and retry the SAME operation before giving up.
          // getValidToken(true) forces a refresh via the refresh token, so a tab that slept past
          // the 1 h expiry recovers transparently (no toast, no reload). Guarded to a single retry
          // so a genuinely invalid session falls through to a clean sign-out instead of looping.
          if (hasAuthError && !isAvdOnlyFeed && !operation.getContext()['_authRetried']) {
            return fromPromise(authService.getValidToken(true).then((t) => t ?? '')).flatMap((fresh) => {
              operation.setContext(({ headers = {} }) => ({
                _authRetried: true,
                headers: { ...headers, Authorization: `Bearer ${fresh}` },
              }));
              if (!fresh) { authService.signOut(); }
              return forward(operation);
            });
          }

          if (graphQLErrors) {
            graphQLErrors.forEach(({ message, locations, path }) => {
              if (isAuthExpired(message)) {
                // Already retried once and still failing -> the session is truly invalid.
                const expMessage = 'Your session has expired, please log in again.';
                alertService.error(expMessage);
                problemsInterceptorService.intercept({ message: expMessage }, undefined, SeverityError.ERROR);
                timer(2000).subscribe(() => { authService.signOut(); });
                return;
              }
              const msg = `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`;
              alertService.error(msg);
            });
          }

          if (networkError) {
            if (networkError instanceof HttpErrorResponse) {
              if (networkError.status === 401) {
                authService.signOut();
              }
              netErrorMsgs.forEach((msg: string) => {
                if (isAvdOnlyFeed) { return; }
                if (isAuthExpired(msg)) { return; } // handled by the retry / sign-out branch above
                const timerObs = timer(2).subscribe(() => {
                  alertService.error(msg);
                  timerObs.unsubscribe();
                });
              });
            } else {
              console.log(`[Network error]: ${networkError}`);
            }
          }
          return;
        });
        const http = ApolloLink.from([basic, auth, errorLink, httpLink.create({ uri, withCredentials: true })]);
        // using the ability to split links, you can send data to each link
        // depending on what kind of operation is being sent
        const link = split(
          // split based on operation type
          ({ query }) => {
            const operationDefinitionNode = getMainDefinition(query);
            return (
              operationDefinitionNode.kind === 'OperationDefinition' && operationDefinitionNode.operation === 'subscription'
            );
          },
          ws,
          http
        );
        return {
          link, cache: new InMemoryCache(),
        };
      },
      deps: [HttpLink, AuthService, AlertService, AuthCookieService],
    },
    CookieService
  ],
})
export class GraphQLModule { }
